import React from 'react';
import {
    Alert,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    FormGroup,
    FormLabel,
    InputLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    Tooltip,
    Typography,
} from '@mui/material';

import { DataAPIContext, GET_ONE } from '../api';

/**
 * Options dialog for the XML/JSON entity export (the import-compatible format).
 *
 * Everything is driven by the server contract — hint keys are never hardcoded
 * here. `GET /entity_exports/contract/` describes, per entity, the formats and
 * the optional subtrees ("hints"); all hints default to ON and unchecking one
 * adds it to the exclusion list. The optional import source stamps a <sources>
 * block so the file is self-describing when re-imported.
 *
 * Props:
 *   open, onClose      dialog state
 *   entity             'activity' | 'itinerary' | 'tour' | 'booking' | 'user'
 *   exportDocument     from useGridExport — (format, {excludeHints, sourceId})
 *   exporting          from useGridExport
 *   selectionLabel     optional human text ("all 1,234 matching", "3 selected")
 *   initialFormat      'xml' | 'json' — the radio to pre-select when the dialog
 *                      opens. A menu that already said "Export as JSON…" must
 *                      not open on XML (CP-8361); without it the last choice
 *                      stays selected.
 */

// The contract is static per deploy — fetch it once per session.
let contractCache = null;

const EntityExportDialog = ({
    open,
    onClose,
    entity,
    exportDocument,
    exporting,
    selectionLabel,
    initialFormat = null,
}) => {
    const dataAPI = React.useContext(DataAPIContext);
    const [contract, setContract] = React.useState(contractCache);
    const [sources, setSources] = React.useState(null);
    const [format, setFormat] = React.useState(initialFormat || 'xml');
    const [excluded, setExcluded] = React.useState(() => new Set());
    const [sourceId, setSourceId] = React.useState('');

    // Re-seed on every open, not just the first: the dialog stays mounted
    // between exports, so its state would otherwise carry over.
    React.useEffect(() => {
        if (open && initialFormat) setFormat(initialFormat);
    }, [open, initialFormat]);

    React.useEffect(() => {
        if (!open) return;
        if (!contractCache) {
            dataAPI(GET_ONE, 'entity_exports', { urlSuffix: 'contract/' })
                .then((res) => {
                    contractCache = res.data;
                    setContract(res.data);
                })
                .catch((e) => console.error('EntityExportDialog: contract fetch failed:', e));
        }
        if (sources === null) {
            dataAPI(GET_ONE, 'import_sources', { urlSuffix: '' })
                .then((res) => setSources(Array.isArray(res.data) ? res.data : []))
                .catch(() => setSources([]));
        }
    }, [open, dataAPI, sources]);

    const entityContract = (contract || {})[entity] || { formats: ['xml', 'json'], hints: [] };

    const toggleHint = (key) => setExcluded((prev) => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key); else next.add(key);
        return next;
    });

    const run = async () => {
        const res = await exportDocument(format, {
            excludeHints: Array.from(excluded),
            sourceId: sourceId || null,
        });
        if (res) onClose();
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Export as XML / JSON</DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Produces a file in the platform import format — it can be re-imported
                    as-is. The download appears here and under Background Tasks when ready.
                </Typography>
                {selectionLabel && (
                    <Alert severity="info" sx={{ mb: 2 }}>Exporting {selectionLabel}.</Alert>
                )}
                <FormControl sx={{ mb: 2 }}>
                    <FormLabel>Format</FormLabel>
                    <RadioGroup row value={format} onChange={(ev) => setFormat(ev.target.value)}>
                        {entityContract.formats.map((fmt) => (
                            <FormControlLabel
                                key={fmt}
                                value={fmt}
                                control={<Radio size="small" />}
                                label={fmt.toUpperCase()}
                            />
                        ))}
                    </RadioGroup>
                </FormControl>
                {sources && sources.length > 0 && (
                    <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                        <InputLabel id="entity-export-source-label">Import source (optional)</InputLabel>
                        <Select
                            labelId="entity-export-source-label"
                            label="Import source (optional)"
                            value={sourceId}
                            onChange={(ev) => setSourceId(ev.target.value)}
                        >
                            <MenuItem value=""><em>None</em></MenuItem>
                            {sources.map((s) => (
                                <MenuItem key={s.code} value={s.code}>{s.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}
                {entityContract.hints.length > 0 && (
                    <FormControl component="fieldset" variant="standard">
                        <FormLabel component="legend">Include</FormLabel>
                        <FormGroup>
                            {entityContract.hints.map((hint) => (
                                <Tooltip key={hint.key} title={hint.description || ''} placement="left">
                                    <FormControlLabel
                                        control={(
                                            <Checkbox
                                                size="small"
                                                checked={!excluded.has(hint.key)}
                                                onChange={() => toggleHint(hint.key)}
                                            />
                                        )}
                                        label={hint.label}
                                    />
                                </Tooltip>
                            ))}
                        </FormGroup>
                    </FormControl>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button
                    onClick={run}
                    variant="contained"
                    disabled={exporting}
                    startIcon={exporting ? <CircularProgress size={14} /> : null}
                >
                    Export
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EntityExportDialog;
